import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const FrictionPointHeatmap = ({ dateRange, refreshTrigger }) => {
  const [frictionData, setFrictionData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFrictionData();

    // Set up real-time subscription
    const channel = supabase?.channel('friction_points')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'service_incidents'
        },
        () => {
          loadFrictionData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [dateRange, refreshTrigger]);

  const getDateFilter = () => {
    const now = new Date();
    let startDate;

    switch (dateRange) {
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return null;
    }

    return startDate?.toISOString();
  };

  const loadFrictionData = async () => {
    setLoading(true);
    try {
      let startDate = getDateFilter();

      // Build query with date filter
      let query = supabase?.from('service_incidents')?.select('*')?.order('detected_at', { ascending: false });

      if (startDate) {
        query = query?.gte('detected_at', startDate);
      }

      const { data: incidents, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error loading friction data:', error?.message);
          setFrictionData([]);
          return;
        }
      }

      if (incidents && incidents?.length > 0) {
        processFrictionData(incidents);
      } else {
        setFrictionData([]);
      }
    } catch (error) {
      console.error('Error loading friction point data:', error);
      setFrictionData([]);
    } finally {
      setLoading(false);
    }
  };

  const isSchemaError = (error) => {
    if (!error) return false;
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') return true;
    }
    return false;
  };

  const processFrictionData = (incidents) => {
    // Group by service and incident type
    const frictionMap = {};

    incidents?.forEach(incident => {
      const key = `${incident?.service_name}-${incident?.incident_type}`;
      const metadata = incident?.metadata || {};

      if (!frictionMap?.[key]) {
        frictionMap[key] = {
          screen: formatServiceName(incident?.service_name),
          frictionPoint: incident?.incident_type || 'Unknown Error',
          severity: getSeverityNumber(incident?.severity),
          occurrences: 0,
          avgTimeSpent: 0,
          abandonmentRate: 0,
          totalTimeSpent: 0,
          resolvedCount: 0
        };
      }

      frictionMap[key].occurrences++;
      
      // Calculate time spent (time to resolution)
      if (incident?.resolved_at && incident?.detected_at) {
        const timeSpent = (new Date(incident?.resolved_at) - new Date(incident?.detected_at)) / 1000;
        frictionMap[key].totalTimeSpent += timeSpent;
      }

      // Track resolved incidents
      if (incident?.status === 'resolved' || incident?.status === 'closed') {
        frictionMap[key].resolvedCount++;
      }
    });

    // Calculate averages and abandonment rates
    const processedData = Object.values(frictionMap)?.map(item => {
      const avgTimeSpent = item?.occurrences > 0 ? Math.round(item?.totalTimeSpent / item?.occurrences) : 0;
      const abandonmentRate = item?.occurrences > 0 
        ? Math.round(((item?.occurrences - item?.resolvedCount) / item?.occurrences) * 100)
        : 0;

      return {
        screen: item?.screen,
        frictionPoint: item?.frictionPoint,
        severity: item?.severity,
        occurrences: item?.occurrences,
        avgTimeSpent,
        abandonmentRate
      };
    });

    // Sort by severity and occurrences
    processedData?.sort((a, b) => {
      if (b?.severity !== a?.severity) return b?.severity - a?.severity;
      return b?.occurrences - a?.occurrences;
    });

    setFrictionData(processedData);
  };

  const formatServiceName = (serviceName) => {
    const nameMap = {
      'openai': 'AI Chat Interface',
      'perplexity': 'AI Search Interface',
      'twilio': 'SMS Notifications',
      'stripe': 'Billing Management'
    };
    return nameMap?.[serviceName] || serviceName?.charAt(0)?.toUpperCase() + serviceName?.slice(1);
  };

  const getSeverityNumber = (severity) => {
    const severityMap = {
      'critical': 5,
      'high': 4,
      'medium': 3,
      'low': 2
    };
    return severityMap?.[severity] || 1;
  };

  const getSeverityColor = (severity) => {
    if (severity >= 5) return 'bg-red-500';
    if (severity >= 4) return 'bg-orange-500';
    if (severity >= 3) return 'bg-yellow-500';
    if (severity >= 2) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getSeverityLabel = (severity) => {
    if (severity >= 5) return 'Critical';
    if (severity >= 4) return 'High';
    if (severity >= 3) return 'Medium';
    if (severity >= 2) return 'Low';
    return 'Minimal';
  };

  const getSeverityTextColor = (severity) => {
    if (severity >= 5) return 'text-red-500';
    if (severity >= 4) return 'text-orange-500';
    if (severity >= 3) return 'text-yellow-500';
    if (severity >= 2) return 'text-blue-500';
    return 'text-green-500';
  };

  const getTotalOccurrences = () => {
    return frictionData?.reduce((sum, item) => sum + item?.occurrences, 0);
  };

  const getAverageAbandonmentRate = () => {
    if (frictionData?.length === 0) return '0.0';
    const avg = frictionData?.reduce((sum, item) => sum + item?.abandonmentRate, 0) / (frictionData?.length || 1);
    return avg?.toFixed(1);
  };

  const getCriticalIssuesCount = () => {
    return frictionData?.filter(item => item?.severity >= 4)?.length;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-warm p-6">
        <p className="text-muted-foreground text-center">Loading friction data...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm p-6 space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertTriangle" size={20} color="var(--color-red-500)" />
            <span className="text-sm text-muted-foreground font-caption">Critical Issues</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getCriticalIssuesCount()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={20} color="var(--color-orange-500)" />
            <span className="text-sm text-muted-foreground font-caption">Total Occurrences</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getTotalOccurrences()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="XCircle" size={20} color="var(--color-red-500)" />
            <span className="text-sm text-muted-foreground font-caption">Avg Abandonment</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getAverageAbandonmentRate()}%</p>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground font-heading">Friction Points by Screen</h3>
          <div className="flex items-center gap-4 text-xs font-caption">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-muted-foreground">Low</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-muted-foreground">Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-muted-foreground">High</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-muted-foreground">Critical</span>
            </div>
          </div>
        </div>

        {frictionData?.length === 0 ? (
          <div className="bg-muted/30 rounded-lg p-8 text-center">
            <p className="text-muted-foreground">No friction points detected for the selected date range</p>
          </div>
        ) : (
          <div className="space-y-2">
            {frictionData?.map((item, index) => (
              <div 
                key={index}
                className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-2 h-2 rounded-full ${getSeverityColor(item?.severity)}`}></div>
                      <h4 className="text-sm font-semibold text-foreground font-heading">
                        {item?.screen}
                      </h4>
                      <span className={`text-xs font-medium font-caption ${getSeverityTextColor(item?.severity)}`}>
                        {getSeverityLabel(item?.severity)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground font-caption mb-3">
                      {item?.frictionPoint}
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <span className="text-muted-foreground font-caption">Occurrences:</span>
                        <span className="ml-1 font-semibold text-foreground">{item?.occurrences}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground font-caption">Avg Time:</span>
                        <span className="ml-1 font-semibold text-foreground">{item?.avgTimeSpent}s</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground font-caption">Abandonment:</span>
                        <span className="ml-1 font-semibold text-red-500">{item?.abandonmentRate}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div 
                      className={`w-16 h-16 rounded-lg ${getSeverityColor(item?.severity)} opacity-20 flex items-center justify-center`}
                    >
                      <span className={`text-2xl font-bold ${getSeverityTextColor(item?.severity)}`}>
                        {item?.severity}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FrictionPointHeatmap;